import { ThemeProps } from 'types/styled';
import Box from 'components/elements/Box';
import styled, { css } from 'styled-components/macro';
import modularScale from 'styles/helpers/modular-scale';
import colors from 'styles/themes/colors';
import Button from 'components/elements/button';
import { primaryTypography } from 'styles/global/typography/typography';
import 'react-datepicker/dist/react-datepicker.css';
import { MenuRoot } from 'components/base/Menu';
import { ButtonWrapper } from 'components/base/Dropdown/shared/styles';
import variables from 'styles/themes/variables';
import { Head } from 'components/data-visualization/Table/SortingHead';
import Body from 'components/data-visualization/Table/Body';
import ColumnHeader from 'components/data-visualization/Table/Header';
import Cell from 'components/data-visualization/Table/Cell';
import Row from 'components/data-visualization/Table/Row';

export const OpenPopupButton = styled.button<any>`
  appearance: none;
  background: transparent;
  font-size: ${modularScale(-2)};
  color: #fff;
  letter-spacing: 0.1em;
  text-align: center;
  border-radius: 0.2em;
  font-weight: 600;
  line-height: 1.6;
  text-transform: uppercase;
  box-sizing: content-box;
  position: relative;
  white-space: nowrap;
  margin: ${({ theme }): string => theme.spacing.small};
  border: 1px solid #fff;
  margin-left: ${({ theme }): string => theme.spacing.xsmall};
  padding: ${({ theme }): string =>
    `0 ${theme.spacing.small} 0 ${theme.spacing.small}`};
  display: flex;
  align-items: center;
  cursor: pointer;

  &.placeholder {
    visibility: hidden;
  }

  &:disabled {
    opacity: 0.5;
  }

  & svg {
    vertical-align: middle;
    margin: 2px 4px 2px 0;
    color: #fff;
    height: 16px;

    @media only screen and (max-width: 25em) {
      margin-right: 0;
    }

    g {
      stroke: #fff;
    }
  }
`;

export const ModalCloseButton = styled(Button)`
  & svg {
    transform: scale(1.8) translate(-6px, -6px);
    opacity: 0.4;
  }
`;

export const FeedTypeSection = styled.div`
  width: 100%;
`;

export const ViewRow = styled(Box)<{ editMode?: boolean }>`
  ${primaryTypography};
  color: ${colors.LightNightBlue};
  display: flex;
  align-items: flex-start;

  & span {
    font-weight: 600;
    color: ${colors.NightBlue};
    margin-right: ${({ theme }): string => theme.spacing.small};
  }

  ${({ editMode }): any =>
    editMode &&
    css`
      padding-top: 2px;
      justify-content: space-between;
      border-bottom: 1px solid ${colors.LightGrey};
      padding: 0.5em 0;

      ${MenuRoot} {
        width: 70%;

        ${ButtonWrapper} {
          height: 32px;
        }
      }

      ${FeedTypeSection} {
        ${ButtonWrapper} {
          background: none;
          border: 1px solid ${colors.CasperGrey};
          text-align: left;
          padding: 0.57em 0.5em;

          svg {
            color: ${colors.NightBlue};
          }
        }
        ${MenuRoot} {
          width: 100%;
        }
      }
    `}
`;

export const JobTitle = styled('h3')`
  font-weight: 600;
  color: ${colors.Blue};
  font-size: 1.3em;
  margin: 0.5em 0;
`;

export const JobStatus = styled('span')<{ status: string }>`
  color: ${({ theme, status }): string => {
    switch (status) {
      case 'pending':
        return theme.NotificationOrange;
      case 'running':
        return theme.Aquamarine;
      case 'completed':
        return theme.Lime;
      case 'failed':
        return theme.DarkRose;
      default:
        return theme.DeepBlue;
    }
  }} !important;
  text-transform: capitalize;
`;

export const DatePickerWrapper = styled(Box)`
  padding-right: ${({ theme }): string => theme.spacing.large};
  & .react-datepicker-wrapper {
    padding-top: ${({ theme }): string => theme.spacing.xxsmall};
    & input {
      padding: 0.3em 0.5em;
    }
  }
`;

export const DateLabel = styled('p')`
  margin: 0;
  line-height: 1;
  font-size: ${modularScale(-1)};
`;

export const ListItem = styled.li<ThemeProps<'li'>>`
  display: flex;
  flex-direction: row;
  position: relative;

  padding: 0.8em 1.5em;
  cursor: pointer;
  border-bottom: 1px solid ${colors.LightGrey};

  ${JobStatus} {
    display: flex;
    justify-self: flex-end;
    margin-left: auto;
    margin-right: 2em;
    font-size: 0.8rem;
    align-self: center;
  }

  & span {
    display: flex;
    margin-left: 1rem;
    color: ${colors.CasperGrey};
    font-size: 0.7rem;
    align-self: center;
  }

  :last-of-type {
    border-bottom: none;
  }

  &:hover {
    background: ${colors.VeryLightGrey};

    &::before {
      right: 1.8em;
    }
  }

  &::before {
    content: '';
    display: block;
    display: block;
    margin: 10px auto;
    width: 10px;
    height: 10px;
    border-top: 2px solid ${colors.CasperGrey};
    border-left: 2px solid ${colors.CasperGrey};
    transform: rotate(135deg);
    position: absolute;
    right: 2em;
    top: 0.6em;
  }
`;

export const Input = styled('input')`
  appearance: none;
  width: 70%;
  background: transparent;
  border: 1px solid ${colors.CasperGrey};
  border-radius: 0;
  box-shadow: none;
  outline: none;
  display: flex;
  justify-self: flex-end;

  font-size: 1em;
  font-family: inherit;
  overflow: auto;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
    opacity: 1;
  }

  ::-ms-clear {
    display: none;
  }
`;

export const Variants = styled('p')`
  color: ${({ theme }): string => theme.NightBlue};
  text-transform: uppercase;
  font-size: 11px;
  margin: 0.5em 0;
`;

export const Message = styled('p')`
  color: ${({ theme }): string => theme.CasperGrey};
  text-align: center;
`;

export const VariantElement = styled(Box)<{
  isSelected?: boolean;
  in4Rows?: boolean;
}>`
  display: inline-block;
  background-color: ${({ isSelected, theme }): string =>
    isSelected ? theme.Lime : theme.Sand};
  cursor: pointer;
  padding: 0.25rem;
  margin: 0.5px;
  width: ${({ in4Rows }): string => `${in4Rows ? 24 : 49}%;`};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

export const GoBackButton = styled.button<ThemeProps<'button'>>`
  padding: ${variables.spacing.small} ${variables.spacing.small} 0;
  color: ${colors.NightBlue};
  width: 100%;
  text-align: left;

  svg {
    transform: rotate(0.5turn);
    margin-right: ${variables.spacing.small};
  }
`;

export const TableHeader = styled(Head)`
  padding: ${variables.spacing.small};
  background-color: ${colors.LightGrey};

  ${ColumnHeader} {
    text-align: center;
    padding: ${variables.spacing.small};
    line-height: 1;
    vertical-align: middle;
    font-size: 13px;
  }
`;

export const TableBody = styled(Body)`
  padding: ${variables.spacing.small};
  ${Row} {
    ${Cell} {
      border-bottom: 1px solid ${colors.LightGrey};
      padding: ${variables.spacing.small};
      text-align: center;
      font-size: 14px;

      &:nth-child(3) {
        line-height: 1.3;
      }
    }

    &:last-of-type {
      ${Cell} {
        border-bottom: none;
      }
    }
  }
`;
